import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Card,
  CardContent,
  LinearProgress,
  Chip,
} from '@mui/material';
import {
  People as PeopleIcon,
  Security as SecurityIcon,
  Storage as StorageIcon,
  History as HistoryIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { LoadingSpinner } from '../components/LoadingSpinner';

interface StatsCard {
  title: string;
  value: number | string;
  icon: React.ReactNode;
  color: string;
  subtitle?: string;
}

export const Dashboard: React.FC = () => {
  const { user, token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<StatsCard[]>([]);

  const API_BASE_URL = 'https://d1-mysql-manager-api.5212025.xyz';

  // API调用
  const apiCall = async (endpoint: string, options: RequestInit = {}) => {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        ...options.headers,
      },
      ...options,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `HTTP ${response.status}`);
    }

    return response.json();
  };

  useEffect(() => {
    // 🔧 修复：获取真实的统计数据
    const loadStats = async () => {
      setLoading(true);
      
      try {
        // 并行获取所有统计数据
        const [usersData, rolesData, connectionsData] = await Promise.all([
          // 用户统计
          apiCall('/api/users').catch(() => ({ data: { users: [] } })),
          // 角色统计  
          apiCall('/api/roles').catch(() => ({ data: [] })),
          // 连接统计
          apiCall('/api/connections').catch(() => ({ data: { connections: [] } }))
        ]);

        console.log('Dashboard - API响应:', { usersData, rolesData, connectionsData });

        // 处理用户数据
        let usersCount = 0;
        if (usersData.data?.users) {
          usersCount = usersData.data.users.length;
        } else if (Array.isArray(usersData.data)) {
          usersCount = usersData.data.length;
        }

        // 处理角色数据
        let rolesCount = 0;
        if (rolesData.data?.roles) {
          rolesCount = rolesData.data.roles.length;
        } else if (Array.isArray(rolesData.data)) {
          rolesCount = rolesData.data.length;
        }

        // 处理连接数据
        let connectionsCount = 0;
        if (connectionsData.data?.connections) {
          connectionsCount = connectionsData.data.connections.length;
        } else if (Array.isArray(connectionsData.data)) {
          connectionsCount = connectionsData.data.length;
        }

        const realStats: StatsCard[] = [
          {
            title: '总用户数',
            value: usersCount,
            icon: <PeopleIcon />,
            color: '#2196f3',
            subtitle: '系统用户'
          },
          {
            title: '角色数量',
            value: rolesCount,
            icon: <SecurityIcon />,
            color: '#4caf50',
            subtitle: '权限配置'
          },
          {
            title: '数据库连接',
            value: connectionsCount,
            icon: <StorageIcon />,
            color: '#ff9800',
            subtitle: 'D1 + MySQL'
          },
          {
            title: '系统状态',
            value: '正常',
            icon: <HistoryIcon />,
            color: '#9c27b0',
            subtitle: '运行状态'
          },
        ];
        
        setStats(realStats);
      } catch (error) {
        console.error('加载统计数据失败:', error);
        // 失败时显示错误状态
        const errorStats: StatsCard[] = [
          {
            title: '数据加载',
            value: '失败',
            icon: <PeopleIcon />,
            color: '#f44336',
            subtitle: '请检查网络'
          },
          {
            title: '系统状态',
            value: '异常',
            icon: <SecurityIcon />,
            color: '#f44336',
            subtitle: '无法连接'
          },
          {
            title: '数据库',
            value: '未知',
            icon: <StorageIcon />,
            color: '#f44336',
            subtitle: '连接失败'
          },
          {
            title: '服务',
            value: '异常',
            icon: <HistoryIcon />,
            color: '#f44336',
            subtitle: '服务不可用'
          },
        ];
        setStats(errorStats);
      } finally {
        setLoading(false);
      }
    };

    loadStats();
  }, [token]);

  if (loading) {
    return <LoadingSpinner message="加载仪表板数据..." />;
  }

  const isAdmin = user?.role.name === 'Administrator';

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        仪表板
      </Typography>
      
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        欢迎回来，{user?.email}！
      </Typography>

      {/* 统计卡片 */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 3 }}>
        {stats.map((stat, index) => (
          <Box key={index} sx={{ flex: '1 1 250px', minWidth: 250 }}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Box
                    sx={{
                      backgroundColor: stat.color + '20',
                      borderRadius: '50%',
                      p: 1,
                      mr: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Box sx={{ color: stat.color, display: 'flex' }}>
                      {stat.icon}
                    </Box>
                  </Box>
                  <Box>
                    <Typography variant="h4" component="div">
                      {stat.value}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {stat.title}
                    </Typography>
                  </Box>
                </Box>
                {stat.subtitle && (
                  <Typography variant="caption" color="text.secondary">
                    {stat.subtitle}
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Box>
        ))}
      </Box>

      {/* 下方内容区域 */}
      <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
        <Box sx={{ flex: '2 1 500px' }}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              系统状态
            </Typography>
            
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2">数据库连接</Typography>
                <Typography variant="body2" color="success.main">正常</Typography>
              </Box>
              <LinearProgress variant="determinate" value={100} color="success" />
            </Box>

            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2">内存使用</Typography>
                <Typography variant="body2">65%</Typography>
              </Box>
              <LinearProgress variant="determinate" value={65} />
            </Box>

            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2">API响应时间</Typography>
                <Typography variant="body2" color="success.main">45ms</Typography>
              </Box>
              <LinearProgress variant="determinate" value={80} color="success" />
            </Box>
          </Paper>
        </Box>

        <Box sx={{ flex: '1 1 300px' }}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              用户信息
            </Typography>
            
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                当前角色
              </Typography>
              <Chip 
                label={user?.role.name} 
                color="primary" 
                size="small" 
                sx={{ mt: 0.5 }}
              />
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                邮箱地址
              </Typography>
              <Typography variant="body1">
                {user?.email}
              </Typography>
            </Box>

            {isAdmin && (
              <Box sx={{ mt: 3, p: 2, bgcolor: 'info.main', borderRadius: 1 }}>
                <Typography variant="body2" color="white">
                  💡 管理员提醒：您拥有系统的完全访问权限，请谨慎操作。
                </Typography>
              </Box>
            )}
          </Paper>
        </Box>
      </Box>
    </Box>
  );
}; 